import {
    createAccrualReviewViewConfig,
    createBalanceSheetViewConfig,
    createDocumentRetrievalViewConfig,
    createForm1099ViewConfig,
    createProfitLossViewConfig,
    createReconciliationViewConfig,
    createTransactionsViewConfig,
    createUncategorizedReviewViewConfig,
} from "@common/helpers/close-template-task-view-config";
import { CloseTemplateBasis } from "@common/types/closeTemplateBasis";
import { TaskType } from "@common/types/task";
import { getTaskPreviewDefinition } from "@components/drawers/taskCreationPreviewRegistry";
import {
    ACCRUAL_REVIEW_PRESET_TO_BUCKET,
    TRANSACTION_PRESET_UNCATEGORIZED,
    isAccrualReviewPreset,
    isTransactionPreset,
    resolveTaskTypeFromOption,
    type TaskTypeOption,
} from "./closeTemplateTasksDrawer.constants";
import { TRANSACTION_PRESET_FILTERS } from "./closeTemplateTaskViewConfig";

/**
 * The view config a workflow option starts with when it is picked, or
 * `undefined` when the option has no default view config.
 */
export const createDefaultViewConfigForTaskTypeOption = ({
    option,
    basis,
}: {
    option: TaskTypeOption;
    basis: CloseTemplateBasis | null;
}): Record<string, unknown> | undefined => {
    const resolvedType = resolveTaskTypeFromOption(option);
    const registryDefaultView =
        resolvedType === TaskType.TRANSACTIONS
            ? undefined
            : getTaskPreviewDefinition(resolvedType)?.createDefaultView;
    if (option === TRANSACTION_PRESET_UNCATEGORIZED) {
        return createUncategorizedReviewViewConfig();
    }
    if (isTransactionPreset(option)) {
        return createTransactionsViewConfig({
            filters: TRANSACTION_PRESET_FILTERS[option],
            basis,
        });
    }
    if (option === TaskType.PROFIT_LOSS) {
        return createProfitLossViewConfig();
    }
    if (option === TaskType.BALANCE_SHEET) {
        return createBalanceSheetViewConfig();
    }
    if (option === TaskType.FORM_1099) {
        return createForm1099ViewConfig();
    }
    if (registryDefaultView) {
        return registryDefaultView();
    }
    if (option === TaskType.DOCUMENT_RETRIEVAL) {
        return createDocumentRetrievalViewConfig();
    }
    if (option === TaskType.RECONCILIATION) {
        return createReconciliationViewConfig();
    }
    if (option === TaskType.TRANSACTION_REVIEW) {
        return { ...createTransactionsViewConfig({ basis }), reviewFields: [] };
    }
    if (isAccrualReviewPreset(option)) {
        return createAccrualReviewViewConfig(
            ACCRUAL_REVIEW_PRESET_TO_BUCKET[option],
        );
    }
    return undefined;
};
